// FEAT-12 (spec §3.6) — what authoring a coding question looks like, per
// language, in ONE table. The Python / JavaScript / SQL strings are the exact
// ones that lived in QuestionForms.tsx and CodeEditorModal.tsx (moved, not
// reworded); every program-mode language (the ones that run on our servers)
// comes from `programDefaults`. authoring.test.ts proves every language in the
// registry has every field — a language added to registry.json without its
// authoring copy reds there, not in a recruiter's form.
//
// Copy is a placeholder until Emilia's wording lands (CLAUDE.md: wording is hers).
import { languageEntry, languageLabel, REGISTRY, type Language } from "./registry";

export interface LanguageAuthoring {
  /** Applied to a blank starter on language change (program languages only). */
  starterTemplate: string;
  starterPlaceholder: string;
  starterFilename: string;
  starterHelp: string;
  /** The question form's test-row placeholders. */
  testPlaceholders: { input: string; expected: string };
  /** The code editor's test-row placeholders. */
  editorTestPlaceholders: { input: string; expected: string };
  /** The editor's one-line "what the tests mean" guide. */
  inputGuide: string;
  /** The editor's hint when a test input is refused (mirrors `_check_test_case`). */
  literalHint: string;
  tabSize: number;
  /** The per-kind help line under the language picker ("" = none). */
  languageHelp: string;
}

// Mirrors the server-side authoring guard in backend/api/admin.py (TestCaseIn.
// _validate / _check_test_case) per language. Python: the grader calls
// FN(ast.literal_eval(input)), so a call- or kwargs-style input is a latent
// mis-grade, not a runtime nicety — showing the same sentence here means the
// recruiter reads one message whether the failure surfaces at Run or at save.
// JavaScript: the input is the argument LIST, evaluated inside the jail as
// `[input]`; SQL: the input is optional extra statements / a check query and
// the expected output is JSON rows (FEAT-6, spec §3.2–3.3). TypeScript
// (FEAT-12 Build 2) is JavaScript's contract once its types are erased.
const BROWSER: Record<"python" | "javascript" | "sql" | "typescript", LanguageAuthoring> = {
  python: {
    starterTemplate: "",
    starterPlaceholder: "def solution(...):\n    pass",
    starterFilename: "starter.py",
    starterHelp: "The Python scaffold candidates start from.",
    testPlaceholders: { input: "input (a Python literal)", expected: "expected output" },
    editorTestPlaceholders: { input: "[1, 2, 3], 3", expected: "6" },
    inputGuide: "input = one Python literal · expected output = the returned value as text",
    literalHint:
      'Test input must be a Python literal (e.g. 5, "text", [1, 2], {"k": 1}). ' +
      "For multiple arguments pass a tuple — e.g. [1, 2, 3], 3 — not a call like " +
      "add(1, 2) or kwargs.",
    tabSize: 4,
    languageHelp: "",
  },
  javascript: {
    starterTemplate: "",
    starterPlaceholder: "function solution(...) {\n  // ...\n}",
    starterFilename: "starter.js",
    starterHelp:
      "The JavaScript scaffold candidates start from — a plain function, no async, no Node or browser APIs.",
    testPlaceholders: { input: "arguments, e.g. [1, 2, 3], 3", expected: "expected (a JS expression)" },
    editorTestPlaceholders: { input: "[1, 2, 3], 3", expected: "6" },
    inputGuide: "input = the argument list (a, b) · expected output = a JavaScript expression",
    literalHint:
      "Test input must be the argument list as JavaScript values — e.g. [1, 2, 3], 3 " +
      "for two arguments, or 5 for one — and the expected output a JavaScript " +
      "expression (e.g. [250], \"text\", {a: 1}). Not a call like add(1, 2).",
    tabSize: 2,
    languageHelp: "",
  },
  sql: {
    starterTemplate: "",
    starterPlaceholder: "SELECT ...",
    starterFilename: "starter.sql",
    starterHelp:
      "Optional — a query skeleton candidates start from. The tables come from the database setup above.",
    testPlaceholders: {
      input: "extra statements / check query (optional)",
      expected: 'rows as JSON, e.g. [["ann", 15.5]]',
    },
    editorTestPlaceholders: {
      input: "optional: extra statements / check query",
      expected: '[["ann", 15.5]]',
    },
    inputGuide: "input = optional extra statements / check query · expected output = JSON rows",
    literalHint:
      "A SQL test's input is optional — extra statements or a check query that runs " +
      "after the candidate's SQL — and its expected output is the result rows as JSON, " +
      'e.g. [["bob", 20], ["ann", 15.5]]. Rows are compared in order; column names are ignored.',
    tabSize: 2,
    languageHelp: "",
  },
  typescript: {
    starterTemplate: "",
    starterPlaceholder: "function solution(nums: number[]): number {\n  // ...\n}",
    starterFilename: "starter.ts",
    starterHelp:
      "The TypeScript scaffold candidates start from — a plain function, no async, no Node or browser APIs. " +
      "Types are allowed but removed before it runs, never checked.",
    testPlaceholders: { input: "arguments, e.g. [1, 2, 3], 3", expected: "expected (a JS expression)" },
    editorTestPlaceholders: { input: "[1, 2, 3], 3", expected: "6" },
    inputGuide: "input = the argument list (a, b) · expected output = a JavaScript expression",
    literalHint:
      "Test input must be the argument list as JavaScript values — e.g. [1, 2, 3], 3 " +
      "for two arguments, or 5 for one — and the expected output a JavaScript " +
      "expression (e.g. [250], \"text\", {a: 1}). Not a call like add(1, 2).",
    tabSize: 2,
    languageHelp:
      "TypeScript runs in the candidate's browser like JavaScript: its types are removed before the code " +
      "runs and are never checked, so a type error never fails a test. Tests use JavaScript values.",
  },
};

// The source file each program language runs as (runner/languages.py).
const PROGRAM_FILENAME: Partial<Record<Language, string>> = {
  c: "main.c",
  cpp: "main.cpp",
  go: "main.go",
  php: "main.php",
  ruby: "main.rb",
  perl: "main.pl",
  java: "Main.java", // the public class Main lives in Main.java
  rust: "main.rs",
  kotlin: "Main.kt", // the runner's recipe file names (never the language id)
  csharp: "Main.cs",
  dart: "main.dart",
  swift: "main.swift", // FEAT-12 Build 5 — the heavy pool
  scala: "Main.scala", // the object Main lives in Main.scala
  julia: "main.jl", // never the `main.julia` the fallback would print
};

/** Every program-mode (stdin → stdout) language's authoring copy, from its
 *  registry row: the same words for all of them, their name and version in. */
export function programDefaults(language: Language): LanguageAuthoring {
  const entry = languageEntry(language);
  return {
    starterTemplate: entry.starter,
    starterPlaceholder: `A complete ${entry.name} program that reads standard input and prints its answer`,
    starterFilename: PROGRAM_FILENAME[language] ?? `main.${language}`,
    // An interpreted language (no uiHint: nothing compiles) "runs as-is".
    starterHelp: `The complete program candidates start from — it ${
      entry.uiHint ? "compiles" : "runs"
    } as-is, reads standard input and prints nothing yet.`,
    testPlaceholders: { input: "standard input, e.g. 3 then 1 2 3", expected: "expected standard output, e.g. 6" },
    editorTestPlaceholders: { input: "3\n1 2 3", expected: "6" },
    inputGuide: "input = the program's whole standard input · expected output = everything it prints",
    literalHint:
      "A test's input is the program's whole standard input and its expected output everything the " +
      "program prints. Trailing spaces and blank lines at the end are ignored; everything else must match.",
    tabSize: 4,
    languageHelp:
      `${languageLabel(language)} runs on our servers. The candidate writes a whole program that reads ` +
      "standard input and prints its answer; each test is one stdin → expected stdout pair. Trailing " +
      "spaces and blank lines are ignored, everything else must match.",
  };
}

export const AUTHORING: Record<Language, LanguageAuthoring> = Object.fromEntries(
  REGISTRY.map((e) => [
    e.id,
    e.mode === "program" ? programDefaults(e.id) : BROWSER[e.id as keyof typeof BROWSER],
  ]),
) as Record<Language, LanguageAuthoring>;

/** FEAT-12 phase 2 — a TYPED question's authoring copy (Java / PHP with a
 *  signature): the candidate writes one method of `class Solution`, a test is
 *  its arguments as JSON values and the value it returns. The starter comes
 *  from the signature (`typedStarter`), never from a fixed template. Mirrors
 *  `typed_driver.check_typed_test` on the server. Placeholder copy (Emilia's). */
export function typedDefaults(language: Language): LanguageAuthoring {
  const program = programDefaults(language);
  return {
    ...program,
    starterTemplate: "",
    starterPlaceholder: "Set the function signature above — the starter is written from it",
    starterHelp:
      "The class candidates start from, written from the signature — it compiles as-is and returns a " +
      "default value. Edit it freely; keep the method's name and types.",
    testPlaceholders: { input: "arguments, e.g. [2, 7, 11, 15], 9", expected: "returns, e.g. [0, 1]" },
    editorTestPlaceholders: { input: "[2, 7, 11, 15], 9", expected: "[0, 1]" },
    inputGuide: "input = the arguments as JSON values · expected = the value the method returns, as JSON",
    literalHint:
      "A test's input is the method's arguments as JSON values separated by commas, in the signature's " +
      'order — e.g. [2, 7, 11, 15], 9. Strings in double quotes, true / false, numbers and [lists]; no ' +
      "null. The expected value is one JSON value of the return type; floats match within 1e-9.",
    languageHelp:
      `${languageLabel(language)} runs on our servers. The candidate writes one method of class Solution; ` +
      "each test passes its arguments and checks the value it returns.",
  };
}

/** A question's authoring copy — typed (it has a signature) or its language's
 *  own. The ONE read of the typed switch for copy; callers pass their own
 *  `typed`. */
export function authoringFor(language: Language, typed: boolean): LanguageAuthoring {
  return typed ? typedDefaults(language) : AUTHORING[language];
}

/** Whether a starter may be replaced by another language's template on a
 *  language change: only when it is blank or still byte-equal to SOME
 *  language's template — a hand-written starter is never clobbered. */
export function isTemplateStarter(code: string | null | undefined): boolean {
  const text = code ?? "";
  if (!text.trim()) return true;
  return REGISTRY.some((e) => AUTHORING[e.id].starterTemplate && AUTHORING[e.id].starterTemplate === text);
}
